import { ModeEditOutlined as ModeEditOutlinedIcon } from 'components/icons';
import React, { useId, useState } from 'react';
import { cn } from 'utils/cn';

interface EditableTextFieldProps {
    value: string | null | undefined;
    onSave: (newValue: string) => Promise<void>;
    isEditEnabled: boolean;
    placeholder?: string;
    icon?: React.ReactNode;
    leftIcon?: React.ReactNode;
    minWidth?: number;
    maxLength?: number;
    ariaLabel?: string;
    validation?: (value: string) => string | true | Promise<string | true>; // Returns error message or true if valid
    onEditModeChange?: (isEditing: boolean) => void;
}

const EditableTextField: React.FC<EditableTextFieldProps> = ({
    value: initialValue,
    onSave,
    isEditEnabled,
    placeholder = '',
    icon,
    leftIcon,
    minWidth = 150,
    maxLength,
    ariaLabel,
    validation,
    onEditModeChange,
}) => {
    // TODO(react-doctor: rerender-state-only-in-handlers / no-derived-state-effect):
    // `isEditMode` is read in render to swap views, so it must stay useState.
    // The `setValue(initialValue)` resync below is a deliberate draft-state pattern:
    // local `value` is the editing draft, synced from prop on parent updates and
    // submitted on blur. Replacing with a `key`-based reset would change UX (lose
    // unsaved input on parent re-renders) and is intentionally left as-is.
    // oxlint-disable-next-line react-doctor/rerender-state-only-in-handlers -- read in render `if (!isEditEnabled || !isEditMode)` to swap display vs input view
    const [isEditMode, setIsEditMode] = useState(false);
    const [value, setValue] = useState(initialValue || '');
    const [error, setError] = useState<string | null>(null);
    const errorId = useId();

    // Draft-state sync: local `value` is the editing draft. Resync from prop when the parent's saved
    // value changes by tracking the previous prop value at render time
    // (https://react.dev/reference/react/useState#storing-information-from-previous-renders).
    // Preserves the original draft-overwrite trade-off documented above without using an effect.
    const [prevInitialValue, setPrevInitialValue] = useState(initialValue);
    if (initialValue !== prevInitialValue) {
        setPrevInitialValue(initialValue);
        setValue(initialValue || '');
    }

    const handleBlur = async () => {
        if (validation) {
            const validationResult = await validation(value);
            if (validationResult !== true) {
                setError(validationResult);
                return;
            }
        }

        setError(null);
        setIsEditMode(false);
        onEditModeChange?.(false);

        if (value !== (initialValue || '')) {
            try {
                await onSave(value);
            } catch (error) {
                console.error('Failed to save value:', error);
                setValue(initialValue || '');
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            (e.target as HTMLInputElement).blur();
        } else if (e.key === 'Escape') {
            setValue(initialValue || '');
            setError(null);
            setIsEditMode(false);
            onEditModeChange?.(false);
        }
    };

    if (!isEditEnabled || !isEditMode) {
        return (
            <div
                className={cn('inline-flex items-center gap-1', isEditEnabled ? 'cursor-pointer' : 'cursor-default')}
                onClick={() => {
                    if (isEditEnabled) {
                        setIsEditMode(true);
                        onEditModeChange?.(true);
                    }
                }}
                aria-label={ariaLabel}
            >
                {leftIcon}
                <span>{value || placeholder}</span>
                {isEditEnabled && (icon || <ModeEditOutlinedIcon className="text-grey-500 text-[16px]" />)}
            </div>
        );
    }

    return (
        <div className="flex flex-col" style={{ minWidth }}>
            <div className="inline-flex items-center">
                <input
                    type="text"
                    value={value}
                    onChange={(e) => {
                        setValue(e.target.value);
                        if (error) setError(null);
                    }}
                    onBlur={() => {
                        void handleBlur();
                    }}
                    onKeyDown={handleKeyDown}
                    autoFocus
                    maxLength={maxLength}
                    placeholder={placeholder}
                    aria-label={ariaLabel}
                    aria-invalid={!!error}
                    aria-describedby={error ? errorId : undefined}
                    className={cn(
                        'focus-visible:outline-primary placeholder:text-grey-400 new:placeholder:text-muted-foreground w-full rounded-sm border px-2 py-1 font-[inherit] text-base outline-none focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-solid md:text-[length:inherit]',
                        error ? 'border-destructive' : 'border-divider'
                    )}
                />
            </div>
            {error && (
                <div id={errorId} className="text-destructive mt-1 text-xs">
                    {error}
                </div>
            )}
        </div>
    );
};

export default EditableTextField;
